import React, { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { ArrowUpRightIcon } from 'lucide-react';
import { newsStories } from '../data/news';
import { NewsReader } from './NewsReader';
type Story = (typeof newsStories)[number];
const EASE = [0.23, 1, 0.32, 1] as const;
export function News() {
  const [openStory, setOpenStory] = useState<Story | null>(null);
  const closeReader = useCallback(() => setOpenStory(null), []);
  const sorted = useMemo(() => [...newsStories].sort((a, b) => b.date.localeCompare(a.date)), []);
  const [featured, ...rest] = sorted;
  return <section id="news" className="bg-dune py-20 md:py-28" data-id="element-239" data-mp="39d7ogjzo1lm">
      <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-240" data-mp="89hd9bjczbx2">
        <div data-id="element-241" data-mp="w03lb2td4w72">
          <p className="font-script text-4xl text-flamingo" data-id="element-242" data-mp="w0dc2sm8f7dt">Noticias</p>
          <h2 className="mt-1 font-display text-6xl uppercase leading-[0.9] text-ink md:text-8xl" data-id="element-243" data-mp="vy0onzxpg4td">
            News &amp; <span className="text-catracho" data-id="element-244" data-mp="2xrs9slntsmjk">Updates</span>
          </h2>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-245" data-mp="2xpo5i2m0llir">
          {featured && <button type="button" onClick={() => setOpenStory(featured)} className="group text-left lg:col-span-7" data-id="element-246" data-mp="2sql4qud2i1gl">
              <div className="relative overflow-hidden rounded-[2rem]" data-id="element-247" data-mp="32palukx53hvs">
                <img src={featured.image} alt="" style={{
              objectPosition: featured.imagePosition
            }} className="aspect-[16/10] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-248" data-mp="3ghh0g2kn9gt4" />
                <span className="absolute left-5 top-5 rounded-full bg-sun px-4 py-1.5 text-sm font-bold text-ink" data-id="element-249" data-mp="3lieimf5nmaum">
                  Featured
                </span>
              </div>
              <time dateTime={featured.date} className="mt-6 block text-sm font-bold text-ink/60" data-id="element-250" data-mp="1r3vde4tq9t8w">
                {format(parseISO(featured.date), 'MMMM d, yyyy')} · {featured.readTime}
              </time>
              <h3 className="mt-2 font-display text-4xl uppercase leading-[0.95] text-ink transition-colors duration-150 group-hover:text-catracho md:text-5xl" data-id="element-251" data-mp="2ksb20nuygg5r">
                {featured.title}
              </h3>
              <p className="mt-4 max-w-2xl text-lg text-ink/75" data-id="element-252" data-mp="2usac4p70wekq">{featured.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 font-bold text-flamingo" data-id="element-253" data-mp="2zr51k9zhx1dt">
                Read story
                <ArrowUpRightIcon size={18} className="transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" data-id="element-254" data-mp="33qdvbeujcz9d" />
              </span>
            </button>}

          <div className="lg:col-span-5" data-id="element-255" data-mp="3ihd0efw5x5o7">
            <h3 className="font-display text-2xl uppercase text-ink" data-id="element-256" data-mp="3iiedsr0ni493">Latest</h3>
            <ul className="mt-4" data-id="element-257" data-mp="3ngzawhklw7tb">
              {rest.map((story, i) => <motion.li key={story.id} initial={{
              opacity: 0,
              y: 8
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.2,
              delay: 0.05 + i * 0.04,
              ease: EASE
            }} className="border-t border-ink/15" data-id="element-258" data-mp="1bt40y75ky1h1" data-mpi={i}>
                  <button type="button" onClick={() => setOpenStory(story)} className="group flex w-full items-center gap-5 py-5 text-left" data-id="element-259" data-mp="wxocchnrqxs1">
                    <div className="shrink-0 overflow-hidden rounded-2xl" data-id="element-260" data-mp="1kszegimly8k8">
                      <img src={story.image} alt="" style={{
                    objectPosition: story.imagePosition
                  }} className="h-24 w-28 object-cover transition-transform duration-300 ease-out group-hover:scale-105 md:w-32" data-id="element-261" data-mp="1kr69d7rn7wec" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-262" data-mp="1szsgeqp7k4i7">
                      <h4 className="font-bold leading-snug text-ink transition-colors duration-150 group-hover:text-catracho md:text-lg" data-id="element-263" data-mp="1t36h7ui845nj">
                        {story.title}
                      </h4>
                      <time dateTime={story.date} className="mt-1 block text-sm text-ink/60" data-id="element-264" data-mp="1o1qywa7fkd8u">
                        {format(parseISO(story.date), 'MMM d, yyyy')}
                      </time>
                    </div>
                    <ArrowUpRightIcon size={20} className="hidden shrink-0 text-ink/40 transition-[transform,color] duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-flamingo sm:block" aria-hidden="true" data-id="element-265" data-mp="2gqxwn6z28yt1" />
                  </button>
                </motion.li>)}
            </ul>
          </div>
        </div>
      </div>

      <NewsReader story={openStory} onClose={closeReader} data-id="element-266" data-mp="2ziz3owlznvdj" />
    </section>;
}